/**
 * Staff portal — Announcements feed.
 *
 * Full-page rendering of the active HR-announcement feed. The dashboard
 * card shows a compact preview with a 2-line clamp; this page is the
 * "read more" surface where staff can see the full body and attachments
 * without leaving the portal.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@/components/ui';
import { Chip, TextInput } from '@/components/ui/inputs';
import { TypographyStylesProvider } from '@mantine/core';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import {
  IconAlertCircle,
  IconBell,
  IconExternalLink,
  IconPaperclip,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import {
  useStaffPortalAnnouncementService,
  useStaffPortalLookupsService,
  type HRAnnouncementModelView,
  type HRAnnouncementAttachment,
  type HRAnnouncementType,
} from '@actschurch/ccms-sdk';
import { hrKeys } from '../utils/queryKeys';
import { formatDate, formatDateTime, getFileIcon } from '../utils/hrHelpers';

export default function MyAnnouncementsPage() {
  const announcementsService = useStaffPortalAnnouncementService();
  const lookupsService = useStaffPortalLookupsService();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const {
    data: announcements = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: hrKeys.myAnnouncements(50),
    queryFn: () => announcementsService.listActive(50),
  });

  const { data: types = [] } = useQuery({
    queryKey: hrKeys.announcementTypes(),
    queryFn: () => lookupsService.announcementTypes(),
  });

  const filtered = useMemo(() => {
    let out = announcements as HRAnnouncementModelView[];
    if (typeFilter) {
      out = out.filter((a) => String(a.typeID) === typeFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((a) => `${a.title} ${a.body} ${a.typeName ?? ''}`.toLowerCase().includes(q));
    }
    return out;
  }, [announcements, typeFilter, search]);

  return (
    <FadeIn>
      <Container size="xl" py="md">
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Stack gap={2}>
              <Title order={2}>Announcements</Title>
              <Text size="sm" c="dimmed">
                Active HR announcements. Drafts and archived posts never appear here.
              </Text>
            </Stack>
            <Group gap="sm" data-help-id="hr.announcements.actions">
              <Tooltip label="Refresh">
                <ActionIcon variant="subtle" size="lg" onClick={() => refetch()} loading={isFetching}>
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
              <PageHelpButton pageKey="hr.announcements" />
            </Group>
          </Group>

          {/* Toolbar */}
          <Card withBorder padding="md" radius="md" data-help-id="hr.announcements.toolbar">
            <Group justify="space-between" wrap="wrap">
              <Group gap="xs" wrap="wrap" data-help-id="hr.announcements.type-filter">
                <Chip
                  checked={typeFilter == null}
                  onChange={() => setTypeFilter(null)}
                  variant="light"
                >
                  All
                </Chip>
                {(types as HRAnnouncementType[]).map((t) => (
                  <Chip
                    key={t.typeID}
                    checked={typeFilter === String(t.typeID)}
                    onChange={() => setTypeFilter(String(t.typeID))}
                    variant="light"
                  >
                    {t.name}
                  </Chip>
                ))}
              </Group>
              <TextInput
                placeholder="Search title or body"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ minWidth: 240 }}
                wrapperProps={{ 'data-help-id': 'hr.announcements.search' }}
              />
            </Group>
          </Card>

          {/* List */}
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load announcements">
              {(error as Error).message}
            </Alert>
          )}

          <div data-help-id="hr.announcements.feed">
          {isLoading ? (
            <Stack gap="md">
              <Skeleton height={120} />
              <Skeleton height={120} />
              <Skeleton height={120} />
            </Stack>
          ) : filtered.length === 0 ? (
            <Card withBorder padding="xl" radius="md">
              <Stack align="center" gap="sm">
                <IconBell size={32} color="var(--mantine-color-dimmed)" />
                <Text c="dimmed">
                  {announcements.length === 0
                    ? 'No active announcements right now.'
                    : 'No announcements match the current filter.'}
                </Text>
              </Stack>
            </Card>
          ) : (
            <Stack gap="md">
              {filtered.map((a) => (
                <AnnouncementCard key={a.announcementID} announcement={a} />
              ))}
            </Stack>
          )}
          </div>
        </Stack>
      </Container>
    </FadeIn>
  );
}

interface CardProps {
  announcement: HRAnnouncementModelView;
}

function AnnouncementCard({ announcement: a }: CardProps) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <Title order={4} style={{ wordBreak: 'break-word' }}>{a.title}</Title>
              {a.typeName && (
                <Badge
                  size="sm"
                  variant="light"
                  style={a.typeColor ? { backgroundColor: `${a.typeColor}1A`, color: a.typeColor } : undefined}
                >
                  {a.typeName}
                </Badge>
              )}
            </Group>
            <Group gap="md" wrap="wrap">
              {a.publishedAt && (
                <Text size="xs" c="dimmed">Published {formatDateTime(a.publishedAt)}</Text>
              )}
              {a.publishedByDisplayName && (
                <Text size="xs" c="dimmed">By {a.publishedByDisplayName}</Text>
              )}
              {a.expiresAt && (
                <Text size="xs" c="dimmed">Expires {formatDate(a.expiresAt)}</Text>
              )}
            </Group>
          </Stack>
        </Group>

        {/* Body is rich HTML produced by the HtmlEditor in the admin form. */}
        <TypographyStylesProvider>
          <div dangerouslySetInnerHTML={{ __html: a.body ?? '' }} />
        </TypographyStylesProvider>

        {a.attachments && a.attachments.length > 0 && (
          <Stack gap={4}>
            <Group gap={4}>
              <IconPaperclip size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" fw={600} c="dimmed">
                {a.attachments.length} attachment{a.attachments.length > 1 ? 's' : ''}
              </Text>
            </Group>
            <Stack gap={4}>
              {a.attachments.map((att: HRAnnouncementAttachment) => {
                const { Icon, color } = getFileIcon({ fileName: att.fileName });
                return (
                  <Group key={att.attachmentID} justify="space-between" wrap="nowrap" gap="sm">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <ThemeIcon size="md" variant="light" color={color}>
                        <Icon size={16} />
                      </ThemeIcon>
                      <Text size="sm" truncate="end">{att.fileName}</Text>
                    </Group>
                    {att.fileURL && (
                      <Button
                        component="a"
                        href={att.fileURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        size="xs"
                        leftSection={<IconExternalLink size={12} />}
                      >
                        Open
                      </Button>
                    )}
                  </Group>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
